import { Badge } from "@d12labs/ui/badge";
import { Button } from "@d12labs/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@d12labs/ui/card";
import { EmptyState } from "@d12labs/ui/empty-state";
import { Input } from "@d12labs/ui/input";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Pill } from "@d12labs/ui/pill";
import { Skeleton } from "@d12labs/ui/skeleton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ClipboardList } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { speechCoachApi } from "../api";

export function PlanPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const planQuery = useQuery({
		queryKey: ["speech-coach", "plan"],
		queryFn: () => speechCoachApi.getPlan(),
	});

	const drillsQuery = useQuery({
		queryKey: ["speech-coach", "drills"],
		queryFn: () => speechCoachApi.drills(),
		enabled: !!planQuery.data?.plan,
	});

	const plan = planQuery.data?.plan ?? null;
	const assigned = drillsQuery.data?.assigned ?? [];

	const [weeklyTarget, setWeeklyTarget] = useState<string>("");

	// Sync weeklyTarget input when plan loads (only on first load)
	const loadedTarget = plan?.weeklyTarget;
	const targetInputValue =
		weeklyTarget === "" && loadedTarget != null ? String(loadedTarget) : weeklyTarget;

	const patchPlan = useMutation({
		mutationFn: (target: number) => speechCoachApi.patchPlan({ input: { weeklyTarget: target } }),
		onSuccess: () => {
			toast.success("Weekly target updated");
			queryClient.invalidateQueries({ queryKey: ["speech-coach", "plan"] });
		},
		onError: (err) => {
			toast.error(err instanceof Error ? err.message : "Failed to update");
		},
	});

	const handleSave = () => {
		const n = Number(targetInputValue);
		if (!Number.isInteger(n) || n < 2 || n > 7) {
			toast.error("Weekly target must be between 2 and 7");
			return;
		}
		patchPlan.mutate(n);
	};

	if (planQuery.isLoading) {
		return (
			<>
				<PageHeader title="Your Plan" icon={ClipboardList} />
				<PageBody className="p-6 space-y-6 mx-auto max-w-3xl w-full">
					<Skeleton w="100%" h={120} />
					<Skeleton w="100%" h={200} />
				</PageBody>
			</>
		);
	}

	if (!plan) {
		return (
			<>
				<PageHeader title="Your Plan" icon={ClipboardList} />
				<PageBody className="p-6 space-y-6 mx-auto max-w-3xl w-full">
					<EmptyState
						icon={ClipboardList}
						title="No active plan"
						body="Generate a plan from the onboarding page."
						primary={
							<Button onClick={() => navigate({ to: "/speech-coach/onboarding" })}>
								Get started
							</Button>
						}
					/>
				</PageBody>
			</>
		);
	}

	return (
		<>
			<PageHeader
				title="Your Plan"
				icon={ClipboardList}
				actions={
					<Button variant="ghost" asChild>
						<Link to="/speech-coach">Today</Link>
					</Button>
				}
			/>
			<PageBody className="p-6 space-y-6 mx-auto max-w-3xl w-full">
				{/* Focus dimensions */}
				<Card>
					<CardHeader>
						<CardTitle>Focus areas</CardTitle>
					</CardHeader>
					<CardContent className="flex flex-col gap-4">
						<div className="flex flex-wrap gap-2">
							{plan.focusDims.map((dim) => (
								<Pill key={dim}>{dim}</Pill>
							))}
						</div>
						{plan.aiBrief ? <p className="text-body-sm text-ink-subtle">{plan.aiBrief}</p> : null}
					</CardContent>
				</Card>

				{/* Weekly target */}
				<Card>
					<CardHeader>
						<CardTitle>Weekly target</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="flex items-center gap-3">
							<Input
								type="number"
								min={2}
								max={7}
								className="w-24"
								value={targetInputValue}
								onChange={(e) => setWeeklyTarget(e.target.value)}
							/>
							<span className="text-body-sm text-ink-subtle">sessions / week</span>
							<Button
								variant="outline"
								size="sm"
								onClick={handleSave}
								disabled={patchPlan.isPending}
							>
								{patchPlan.isPending ? "Saving…" : "Save"}
							</Button>
						</div>
					</CardContent>
				</Card>

				{/* Drill list */}
				<div className="flex flex-col gap-3">
					<h2 className="text-sm font-semibold text-foreground">
						Drills in your plan ({assigned.length})
					</h2>
					{drillsQuery.isLoading ? (
						<Skeleton w="100%" h={120} />
					) : assigned.length === 0 ? (
						<p className="text-body-sm text-ink-subtle">No drills assigned to this plan yet.</p>
					) : (
						<div className="flex flex-col gap-3">
							{assigned.map((drill) => (
								<Card key={drill.id}>
									<CardContent className="flex flex-col gap-2 pt-4">
										<div className="flex items-center justify-between">
											<span className="text-sm font-medium text-foreground">{drill.title}</span>
											<Badge tone="primary">{drill.category}</Badge>
										</div>
										<p className="text-body-sm text-ink-subtle line-clamp-2">
											{drill.instructions}
										</p>
									</CardContent>
								</Card>
							))}
						</div>
					)}
				</div>
			</PageBody>
		</>
	);
}
